/**
 * Collapsible "How to do it (Property Guide)" for cleaner task view.
 * Shows pinned entries first, then entries matching the current room.
 */
import { useState } from "react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
} from "@/components/ui/dialog";
import { BookOpen, ChevronDown, ChevronRight, X } from "lucide-react";
import type { PropertyGuideEntry } from "@/hooks/usePropertyGuideEntries";
import { getCategoryLabel } from "@/hooks/usePropertyGuideEntries";
import { useTranslation } from "@/hooks/useTranslation";

interface PropertyGuideCardProps {
  entries: PropertyGuideEntry[];
  /** Current room name for filtering (e.g. from checklist room) */
  roomName?: string | null;
  /** Default open when there are pinned or room-matched entries */
  defaultOpen?: boolean;
}

function filterAndSort(
  entries: PropertyGuideEntry[],
  roomName: string | null | undefined
): PropertyGuideEntry[] {
  const pinned = entries.filter((e) => e.pinned);
  const byRoom =
    roomName && roomName.trim()
      ? entries.filter(
          (e) =>
            e.room &&
            e.room.toLowerCase().trim() === roomName.toLowerCase().trim()
        )
      : [];
  const rest = entries.filter(
    (e) => !e.pinned && !byRoom.some((r) => r.id === e.id)
  );
  const seen = new Set<string>();
  const result: PropertyGuideEntry[] = [];
  [...pinned, ...byRoom, ...rest].forEach((e) => {
    if (seen.has(e.id)) return;
    seen.add(e.id);
    result.push(e);
  });
  return result;
}

export default function PropertyGuideCard({
  entries,
  roomName,
  defaultOpen = false,
}: PropertyGuideCardProps) {
  const { tDynamic } = useTranslation();
  const [open, setOpen] = useState(defaultOpen);
  const [viewerEntry, setViewerEntry] = useState<PropertyGuideEntry | null>(null);
  const [viewerPhotoIndex, setViewerPhotoIndex] = useState(0);

  const sorted = filterAndSort(entries, roomName);
  const hasAny = sorted.length > 0;

  if (!hasAny) return null;

  return (
    <>
      <Collapsible open={open} onOpenChange={setOpen}>
        <CollapsibleTrigger asChild>
          <Button
            variant="outline"
            className="w-full justify-between rounded-lg border-blue-200 bg-blue-50/50 hover:bg-blue-100/50"
          >
            <span className="flex items-center gap-2">
              <BookOpen className="h-4 w-4 text-blue-700" />
              {tDynamic("How to do it (Property Guide)")}
            </span>
            {open ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent className="mt-2">
          <div className="rounded-lg border bg-card p-3 space-y-3">
            {sorted.map((entry) => (
              <div
                key={entry.id}
                className="rounded-md border bg-background p-2 space-y-2"
              >
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-sm">{tDynamic(entry.title)}</span>
                  <span className="text-xs text-muted-foreground">
                    {getCategoryLabel(entry.category)}
                  </span>
                </div>
                {entry.instructions && (
                  <p className="text-xs text-muted-foreground line-clamp-2">
                    {tDynamic(entry.instructions)}
                  </p>
                )}
                <div className="flex gap-1.5 flex-wrap">
                  {(entry.photo_urls ?? []).map((url, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => {
                        setViewerEntry(entry);
                        setViewerPhotoIndex(i);
                      }}
                      className="w-14 h-14 rounded border overflow-hidden shrink-0 focus:ring-2 focus:ring-primary"
                    >
                      <img
                        src={url}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                  {entry.video_url && (
                    <a
                      href={entry.video_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-14 h-14 rounded border flex items-center justify-center bg-muted text-xs"
                    >
                      Video
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CollapsibleContent>
      </Collapsible>

      <Dialog
        open={!!viewerEntry}
        onOpenChange={(o) => !o && setViewerEntry(null)}
      >
        <DialogContent className="max-w-[100vw] w-screen max-h-[90vh] overflow-auto p-2">
          {viewerEntry && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <p className="font-medium">{viewerEntry.title}</p>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setViewerEntry(null)}
                  aria-label="Close"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <img
                src={viewerEntry.photo_urls?.[viewerPhotoIndex]}
                alt=""
                className="w-full rounded object-contain max-h-[70vh]"
              />
              {(viewerEntry.photo_urls?.length ?? 0) > 1 && (
                <div className="flex gap-1 flex-wrap justify-center">
                  {(viewerEntry.photo_urls ?? []).map((url, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setViewerPhotoIndex(i)}
                      className={`w-12 h-12 rounded border overflow-hidden shrink-0 ${
                        i === viewerPhotoIndex
                          ? "ring-2 ring-primary"
                          : "opacity-70"
                      }`}
                    >
                      <img
                        src={url}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
